import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ProfilePage from './ProfilePage';
import './SettingsPage.css';

const BIBLE_TRANSLATIONS = [
  { value: 'ESV', label: 'ESV (English Standard Version)', description: 'Literal translation with modern English' },
  { value: 'NIV', label: 'NIV (New International Version)', description: 'Balance of accuracy and readability' },
  { value: 'NASB', label: 'NASB (New American Standard)', description: 'Highly accurate literal translation' },
  { value: 'KJV', label: 'KJV (King James Version)', description: 'Traditional English translation' },
  { value: 'NKJV', label: 'NKJV (New King James)', description: 'Modern update of KJV' },
  { value: 'NLT', label: 'NLT (New Living Translation)', description: 'Thought-for-thought translation' },
  { value: 'CSB', label: 'CSB (Christian Standard Bible)', description: 'Balance of accuracy and clarity' },
];

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showAccount = location.hash === '#account';
  const { 
    preferences, 
    onboarding, 
    updatePreferences, 
    loadPreferences,
    loadOnboardingStatus
  } = useAuth();
  
  const [formData, setFormData] = useState({
    bibleTranslation: 'ESV',
    competitiveMode: false,
    appUsageMode: 'tracking',
    reminderEnabled: true,
    reminderTime: '07:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (preferences) {
      setFormData(prev => ({
        bibleTranslation: preferences.bibleTranslation || 'ESV',
        competitiveMode: preferences.competitiveMode || false,
        appUsageMode: preferences.appUsageMode || 'tracking',
        reminderEnabled: preferences.reminderEnabled !== false,
        reminderTime: preferences.reminderTime || '07:00',
        timezone: preferences.timezone || prev.timezone,
      }));
    }
  }, [preferences]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadPreferences(),
        loadOnboardingStatus()
      ]);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      await updatePreferences({
        ...formData,
        reminderTime: (formData.reminderTime || '07:00').slice(0, 5),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || formData.timezone,
      });
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetOnboarding = async () => {
    if (window.confirm('Are you sure you want to reset your onboarding? This will take you through the setup process again.')) {
      try {
        await userApi.resetOnboarding();
        await loadOnboardingStatus();
        setSaveMessage('Onboarding has been reset. You can go through the setup again.');
        setTimeout(() => setSaveMessage(''), 3000);
      } catch (error) {
        console.error('Failed to reset onboarding:', error);
        setSaveMessage('Failed to reset onboarding. Please try again.');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Customize your ReadTheWord experience</p>
        <div className="settings-tabs" role="tablist" aria-label="Settings">
          <button
            type="button"
            role="tab"
            aria-selected={!showAccount}
            className={`settings-tab ${!showAccount ? 'settings-tab--active' : ''}`}
            onClick={() => navigate('/settings')}
          >
            Preferences
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={showAccount}
            className={`settings-tab ${showAccount ? 'settings-tab--active' : ''}`}
            onClick={() => navigate('/settings#account')}
          >
            Account
          </button>
        </div>
      </div>

      {showAccount && <ProfilePage />}

      {!showAccount && isLoading && (
        <div className="settings-loading">
          <LoadingSpinner size="large" message="Loading settings..." />
        </div>
      )}

      {!showAccount && !isLoading && (
      <>

      {saveMessage && (
        <div className={`settings-message ${saveMessage.includes('Failed') ? 'error' : 'success'}`}>
          {saveMessage}
        </div>
      )}

      <div className="settings-content">
        <form onSubmit={handleSubmit} className="settings-form">
          {/* Bible Translation */}
          <div className="settings-section">
            <div className="section-header">
              <h2>Bible Translation</h2>
              <p>Choose your preferred Bible translation for reading</p>
            </div>
            
            <div className="translation-grid">
              {BIBLE_TRANSLATIONS.map(translation => (
                <div 
                  key={translation.value}
                  className={`translation-option ${formData.bibleTranslation === translation.value ? 'selected' : ''}`}
                  onClick={() => handleChange('bibleTranslation', translation.value)}
                >
                  <div className="translation-name">{translation.label}</div>
                  <div className="translation-description">{translation.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* App Usage Mode */}
          <div className="settings-section">
            <div className="section-header">
              <h2>App Usage Mode</h2>
              <p>How do you plan to use the app?</p>
            </div>
            
            <div className="usage-options">
              <label className="option-card">
                <input 
                  type="radio" 
                  name="appUsageMode" 
                  value="tracking"
                  checked={formData.appUsageMode === 'tracking'}
                  onChange={(e) => handleChange('appUsageMode', e.target.value)}
                />
                <div className="option-content">
                  <div className="option-icon">📊</div>
                  <div className="option-title">Tracking only</div>
                  <div className="option-description">Track reading done elsewhere (physical Bible, other apps)</div>
                </div>
              </label>

              <label className="option-card">
                <input 
                  type="radio" 
                  name="appUsageMode" 
                  value="reading"
                  checked={formData.appUsageMode === 'reading'}
                  onChange={(e) => handleChange('appUsageMode', e.target.value)}
                />
                <div className="option-content">
                  <div className="option-icon">📖</div>
                  <div className="option-title">Reading in app</div>
                  <div className="option-description">Read Bible chapters directly in the app</div>
                </div>
              </label>

              <label className="option-card">
                <input 
                  type="radio" 
                  name="appUsageMode" 
                  value="both"
                  checked={formData.appUsageMode === 'both'}
                  onChange={(e) => handleChange('appUsageMode', e.target.value)}
                />
                <div className="option-content">
                  <div className="option-icon">🔄</div>
                  <div className="option-title">Both</div>
                  <div className="option-description">Mix of tracking and in-app reading</div>
                </div>
              </label>
            </div>
          </div>

          {/* Competitive Mode */}
          <div className="settings-section">
            <div className="section-header">
              <h2>Competitive Features</h2>
              <p>Enable leaderboards and comparisons with family/friends</p>
            </div>
            
            <div className="toggle-option">
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={formData.competitiveMode}
                  onChange={(e) => handleChange('competitiveMode', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <div className="toggle-label">
                <span className="toggle-title">
                  {formData.competitiveMode ? 'Enabled' : 'Disabled'}
                </span>
                <span className="toggle-description">
                  {formData.competitiveMode 
                    ? 'Show rankings, progress comparisons, and achievements'
                    : 'Focus on personal progress without comparisons'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Daily reminder (WS6) — the form only; ENABLE_JOBS is ops. */}
          <div className="settings-section">
            <div className="section-header">
              <h2>Daily Reading Reminder</h2>
              <p>A nudge at your chosen time, only on days you haven&apos;t read yet</p>
            </div>

            <div className="toggle-option">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.reminderEnabled}
                  onChange={(e) => handleChange('reminderEnabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <div className="toggle-label">
                <span className="toggle-title">
                  {formData.reminderEnabled ? 'On' : 'Off'}
                </span>
                <span className="toggle-description">
                  {formData.reminderEnabled
                    ? 'You will also get a nudge in the evening if a streak of 3 or more days is about to break'
                    : 'No reminders of any kind'}
                </span>
              </div>
            </div>

            {formData.reminderEnabled && (
              <div className="reminder-time-row">
                <label htmlFor="reminderTime">Remind me at</label>
                <input
                  id="reminderTime"
                  type="time"
                  value={(formData.reminderTime || '07:00').slice(0, 5)}
                  onChange={(e) => handleChange('reminderTime', e.target.value.slice(0, 5))}
                  step="60"
                />
                <span className="reminder-timezone">{formData.timezone}</span>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="settings-actions">
            <button 
              type="submit"
              className="btn-primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>

        {/* Onboarding Section */}
        <div className="settings-section">
          <div className="section-header">
            <h2>Onboarding</h2>
            <p>Your onboarding status and setup process</p>
          </div>
          
          <div className="onboarding-status">
            <div className="status-info">
              <div className="status-item">
                <span className="status-label">Status:</span>
                <span className={`status-value ${onboarding?.completed ? 'completed' : 'pending'}`}>
                  {onboarding?.completed ? 'Completed' : `Step ${onboarding?.currentStep || 1} of 5`}
                </span>
              </div>
              
              {onboarding?.completedAt && (
                <div className="status-item">
                  <span className="status-label">Completed:</span>
                  <span className="status-value">
                    {new Date(onboarding.completedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              
              {onboarding?.skippedSteps?.length > 0 && (
                <div className="status-item">
                  <span className="status-label">Skipped steps</span>
                  <span className="status-value">
                    {onboarding.skippedSteps.join(', ')}
                  </span>
                </div>
              )}
            </div>
            
            <button 
              type="button"
              className="btn-secondary"
              onClick={handleResetOnboarding}
            >
              Reset Onboarding
            </button>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default SettingsPage;
